/**
 * Response shapes of tracker-api's /api/portal/* routes. Every response is parsed, so a drifted server fails loudly
 * in the client instead of rendering something half-understood. Extra fields are allowed (loose objects).
 */
import { z } from "zod";
import { LANGUAGE_LEVELS, SIGNATURE_MODES } from "@swtracker/core/analyst-profile";
import { TIP_IDS, TOUR_STEP_IDS } from "@swtracker/core/onboarding";

const iso = z.string();
const nullableStr = z.string().nullable();

export const previewTargets = z.object({ available: z.boolean(), analysts: z.array(z.object({ id: z.string(), name: z.string(), upn: z.string() })) });
export const previewAction = z.object({ ok: z.literal(true) });
export const me = z.object({
  preview: z.object({ actor: z.string(), analyst_id: z.string(), expires_at: z.string() }).nullable().optional(),
  name: z.string(), email: z.string(), roles: z.array(z.string()), build_mode: z.boolean().optional(),
  // AIC-181: build mode names the build tester (the BUILD band's "Build tester: {name}"); null in live mode.
  build_tester: z.object({ name: z.string(), upn: z.string() }).loose().nullable().optional(),
}).loose();
export type Me = z.infer<typeof me>;

/** GET / PUT /api/portal/me/onboarding (AIC-302): the caller's own onboarding progress (packages/core/src/onboarding.ts). */
export const onboardingView = z.object({
  version: z.number().int().min(0),
  welcome: z.enum(["auto", "help_only"]),
  welcome_seen: z.boolean(),
  tour: z.object({ step: z.enum(TOUR_STEP_IDS).nullable(), completed: z.boolean(), exited: z.boolean() }),
  dismissed_tips: z.array(z.enum(TIP_IDS)),
  help_opens: z.number().int().min(0),
}).loose();
export type OnboardingViewData = z.infer<typeof onboardingView>;

/** GET /api/portal/me/graph (AIC-158): the caller's own Outlook connection, status only. */
export const graphStatus = z.object({
  available: z.boolean(),
  mapped: z.boolean(),
  connected: z.boolean(),
  reconnect_needed: z.boolean(),
  upn: nullableStr,
  scopes: z.array(z.string()),
  /** AIC-479: the connection holds the calendar scope (older servers omit it). */
  calendar: z.boolean().optional(),
  /** AIC-479: whether vendor call bookings are on (older servers omit it). */
  bookings: z.object({ enabled: z.boolean() }).loose().optional(),
  connected_at: nullableStr,
  last_used_at: nullableStr,
  last_error: nullableStr,
}).loose();
export type GraphStatus = z.infer<typeof graphStatus>;

/**
 * The My profile page (AIC-815): GET /me/profile and GET /analysts/:id/profile, and what a PATCH answers. The codes are
 * @swtracker/core/analyst-profile's; the server checks them, the page only shows them.
 */
const profileChange = z.object({ by: z.string(), by_name: nullableStr, by_role: z.enum(["self", "manager", "admin"]).nullable(), at: z.string() }).loose().nullable();
export const profileView = z.object({
  analyst: z.object({ id: z.string(), name: z.string(), email: nullableStr }).loose(),
  viewer: z.object({ self: z.boolean(), role: z.enum(["analyst", "manager", "admin"]), can_edit: z.array(z.enum(["languages", "regions", "signature", "reminders", "cc"])) }).loose(),
  idc_profile: z.object({
    loaded_at: z.string(), title: nullableStr, bio: nullableStr, research_areas: z.array(z.string()), photo_url: nullableStr, profile_url: nullableStr,
  }).loose().nullable(),
  languages: z.array(z.object({ language: z.string(), level: z.enum(LANGUAGE_LEVELS) }).loose()),
  regions: z.array(z.string()),
  signature: z.object({ mode: z.enum(SIGNATURE_MODES), text: nullableStr }).loose(),
  reminders: z.object({ day3: z.boolean(), day7: z.boolean(), slot: z.boolean() }).loose(),
  extra_cc: z.array(z.string()),
  /** The program mailbox every draft copies; null when it can't be read. */
  program_cc: nullableStr.optional().catch(null),
  changed: z.object({ languages: profileChange, regions: profileChange, signature: profileChange, reminders: profileChange, cc: profileChange }).loose(),
  account: z.object({
    owner_name: nullableStr, kind: z.enum(["owner", "staff"]),
    confirmed: z.object({ by: z.string(), by_name: nullableStr, at: z.string(), source: z.enum(["directory", "admin"]).nullable() }).loose().nullable(),
    role: z.enum(["analyst", "manager", "admin"]).nullable(),
    manager: z.object({ name: nullableStr, upn: z.string() }).loose().nullable(),
  }).loose(),
  report: z.object({ id: z.string(), reason: z.string(), note: nullableStr, at: z.string() }).loose().nullable(),
}).loose();
export type ProfileView = z.infer<typeof profileView>;
export const profileSaved = z.object({ changed: z.array(z.string()), profile: profileView }).loose();
export type ProfileSaved = z.infer<typeof profileSaved>;
export const profileReported = z.object({ existing: z.boolean(), report: z.object({ id: z.string(), reason: z.string(), note: nullableStr, at: z.string() }).loose() }).loose();
/** AIC-987: GET /me/mapping-request and what POST answers: the caller's open request to have their account mapped. */
const mappingRequestItem = z.object({ id: z.string(), at: z.string() }).loose();
/** `can_request` is false for an admin and for a sign-in address another account's row holds (the server refuses those). */
export const mappingRequest = z.object({ mapped: z.boolean(), can_request: z.boolean().optional().default(true), request: mappingRequestItem.nullable() }).loose();
export const mappingRequested = z.object({ existing: z.boolean(), request: mappingRequestItem }).loose();
/** GET /me/profile/hours: the analyst's Outlook working hours (booking's own read), or why there are none to show. */
export const profileHours = z.object({
  state: z.enum(["ok", "default", "not_mapped", "unavailable", "not_connected", "reconnect", "no_calendar", "error"]),
  bookings: z.boolean(),
  days: z.array(z.number()).optional(), start_min: z.number().optional(), end_min: z.number().optional(), tz: z.string().optional(),
}).loose();
export type ProfileHours = z.infer<typeof profileHours>;

/**
 * GET /assignments/:id/analyst-calls (AIC-479): calls the vendor booked with the analyst. The hold on the calendar has
 * no attendees, so nothing was sent: the analyst opens the event and sends the invitation themselves.
 */
const analystCall = z.object({
  id: z.string(),
  status: z.enum(["booked", "cancelled"]),
  start: z.string(),
  end: z.string(),
  upcoming: z.boolean(),
  topic: z.string(),
  topic_label: z.string(),
  detail: nullableStr,
  vendor_email: z.string(),
  vendor_tz: z.string(),
  analyst_name: nullableStr,
  mode: z.enum(["live", "build", "practice"]),
  calendar_upn: nullableStr,
  event_web_link: nullableStr,
  join_url: nullableStr,
  invitation_state: z.enum(["to_send", "update_needed", "cancel_needed"]),
  stale_event_web_link: nullableStr,
}).loose();
export type AnalystCall = z.infer<typeof analystCall>;
export const analystCalls = z.object({ enabled: z.boolean(), calls: z.array(analystCall) }).loose();
export type AnalystCalls = z.infer<typeof analystCalls>;
export const graphConnect = z.object({ url: z.string().regex(/^\/auth\/graph\/connect\?ticket=[A-Za-z0-9_-]+$/) }).loose();
export const graphCheck = z.union([
  z.object({ ok: z.literal(true), upn: nullableStr }).loose(),
  // status / code: Graph's HTTP status and error.code when Graph /me refused (AIC-164).
  z.object({ ok: z.literal(false), reason: z.string(), status: z.number().optional(), code: z.string().optional() }).loose(),
]);
export type GraphCheck = z.infer<typeof graphCheck>;
export const graphDisconnect = z.object({ removed: z.boolean() }).loose();

/** GET /admin/people (AIC-556): everyone's mapping, last sign-in and current-cycle workload (tracker-api people/store.ts). */
export const PERSON_STEPS = ["prepare", "invite", "interview", "review", "complete"] as const;
export type PersonStep = (typeof PERSON_STEPS)[number];
export const personRow = z.object({
  /** The analysts row; null for someone who signed in and has none (kind "signed_in"). */
  id: nullableStr,
  kind: z.enum(["owner", "staff", "signed_in"]),
  owner_name: nullableStr, display_name: nullableStr, upn: nullableStr,
  confirmed_by: nullableStr, confirmed_at: nullableStr, confirmation_source: z.enum(["directory", "admin"]).nullable(),
  suggestion_status: nullableStr, suggested_upn: nullableStr, suggestion_detail: z.unknown(),
  unconfirmed_at: nullableStr, unconfirmed_by: nullableStr, left_at: nullableStr, left_by: nullableStr,
  mapping_state: z.enum(["confirmed", "unconfirmed", "left", "suggestion"]).nullable(),
  manager_upn: nullableStr, manager_name: nullableStr,
  last_seen_at: nullableStr, last_role: z.enum(["admin", "manager", "analyst"]).nullable(),
  /** AIC-815: the person's open "Report a mismatch" items; an older server sends none. */
  reports: z.array(z.object({ id: z.string(), reason: z.string(), note: nullableStr, at: z.string() }).loose()).optional().catch([]),
  work: z.object({
    owned: z.number(), covering: z.number(), open: z.number(), needs_action: z.number(), overdue: z.number(),
    by_step: z.object({ prepare: z.number(), invite: z.number(), interview: z.number(), review: z.number(), complete: z.number() }),
  }),
}).loose();
export type PersonRow = z.infer<typeof personRow>;
export const people = z.object({
  people: z.array(personRow),
  sign_in_history: z.object({ started_at: nullableStr, flag_from: nullableStr }),
  overload_limit: z.number(),
}).loose();
export type People = z.infer<typeof people>;
export const personAssignments = z.object({
  id: z.string(),
  assignments: z.array(z.object({
    id: z.string(), cycle: z.string(), company: z.string(), step: z.enum(PERSON_STEPS), overdue: z.boolean(), covering: z.boolean(), owner_name: nullableStr,
  }).loose()),
}).loose();
export type PersonAssignments = z.infer<typeof personAssignments>;

export const confirmResponse = z.object({ id: z.string(), upn: z.string(), status: z.literal("confirmed") }).loose();
export const unconfirmResponse = z.object({ id: z.string(), status: z.literal("unconfirmed") }).loose();
export const leftResponse = z.object({ id: z.string(), status: z.literal("left"), assignments: z.number() }).loose();
export const unleftResponse = z.object({ id: z.string(), status: z.literal("unleft") }).loose();
export const staffResponse = z.object({ id: z.string(), kind: z.literal("staff"), upn: z.string(), display_name: z.string(), status: z.literal("confirmed") }).loose();
export type StaffResponse = z.infer<typeof staffResponse>;

/** Admin → Cycle → Anomalies (AIC-215): one cycle's anomalies, and a count per kind for the filter. */
export const ANOMALY_STATUSES = ["open", "dismissed", "resolved"] as const;
export type AnomalyStatus = (typeof ANOMALY_STATUSES)[number];
export const anomalyItem = z.object({
  id: z.string(), kind: z.string(), status: z.enum(ANOMALY_STATUSES), detail: z.unknown(), assignment_id: nullableStr, company: nullableStr,
  created_at: z.string(), resolved_at: nullableStr, closed_by: nullableStr, close_reason: nullableStr,
}).loose();
export type AnomalyItem = z.infer<typeof anomalyItem>;
export const anomalies = z.object({
  // AIC-391: kinds on the first page only (a page with a cursor has none; the panel keeps the first page's), and the
  // next page's cursor (null on the last page; absent from an older API).
  cycle: z.string(), kinds: z.array(z.object({ kind: z.string(), open: z.number(), total: z.number() }).loose()).optional(), items: z.array(anomalyItem), truncated: z.boolean(),
  next_cursor: nullableStr.optional(),
}).loose();
export type Anomalies = z.infer<typeof anomalies>;
export const closeAnomalyResponse = z.object({ id: z.string(), status: z.enum(["resolved", "dismissed"]) }).loose();

/** A confirmed analyst: the only valid reassignment target. */
export const confirmedAnalyst = z.object({ id: z.string(), kind: z.enum(["owner", "staff"]), label: z.string(), upn: z.string() }).loose();
export const confirmedAnalysts = z.array(confirmedAnalyst);
export type ConfirmedAnalyst = z.infer<typeof confirmedAnalyst>;
export const reassignResponse = z.object({ id: z.string(), effective_analyst_id: nullableStr, reassigned: z.boolean() }).loose();

/**
 * AIC-181: what the Today queue and the Assignments list add to each row (tracker-api routes/portal-rows.ts).
 * draft_web_link is set only for a draft in the caller's own mailbox; display_status is the status or a derived
 * interview_scheduled / needs_follow_up (not assignment statuses).
 */
/**
 * AIC-252: how an interview link's interview ended (tracker-api sessions/outcome.ts). `outcome` is in_progress while a
 * call is live, review while the vendor confirms, else the last ended attempt's completed / partial / abandoned, or
 * with no outcome recorded: unknown (AIC-269: the interview started, but the link closed before outcomes were tracked)
 * or not_started (no start evidence). end_reason is the gateway's call end reason (dropped, vendor_ended, silence_cap,
 * call_cap, escalate_to_analyst, ...) or form_left, review_left, no_end_report, superseded, expired, submitted.
 */
export const INTERVIEW_OUTCOMES = ["in_progress", "review", "completed", "partial", "abandoned", "unknown", "not_started"] as const;
export const interviewOutcome = z.object({
  session_id: z.string(), outcome: z.string(), end_reason: nullableStr, answered_count: z.number().nullable(), answer_total: z.number().nullable(),
  ended_at: nullableStr, resumable: z.boolean(),
}).loose();
export type InterviewOutcome = z.infer<typeof interviewOutcome>;

const rowExtras = {
  survey_priority: z.string().optional(), thank_you: z.boolean().optional(), owner_id: nullableStr.optional(),
  channel: z.enum(["voice", "form"]).nullable().optional(), scheduled_for: nullableStr.optional(),
  has_draft: z.boolean().optional(), draft_web_link: nullableStr.optional(), open_review_items: z.number().optional(),
  /** AIC-433: when the invite draft was created; Today flags one still unsent after SEND_UNDETECTED_DAYS. */
  draft_created_at: nullableStr.optional().catch(undefined),
  display_status: z.string().optional(),
  /** AIC-252: the latest link's interview outcome; null before any link. */
  interview_outcome: interviewOutcome.nullable().optional().catch(undefined),
};

/** AIC-372: an assignment's latest product event (kind, a plain label, ISO time); null when there is none. */
export const lastActivity = z.object({ kind: z.string(), label: z.string(), at: iso }).loose().nullable().optional().catch(undefined);
export const queueAssignment = z.object({
  cycle: nullableStr.optional(),
  id: z.string(), company: z.string(), survey_type: z.string(), status: z.string(), next_action: nullableStr, updated_at: iso.optional(),
  // AIC-125: what the assignment is due against from its cycle's dates (send by, return by, extension until).
  due_kind: z.enum(["send_by", "return_by", "extension_until"]).nullable().optional(),
  due_on: nullableStr.optional(), days_left: z.number().nullable().optional(), overdue: z.boolean().optional(),
  ...rowExtras,
}).loose();
/**
 * AIC-372: a review item's short copy, as the prototype shows it: title (the analyst's action), issue_label (the
 * "Company · issue type" line) and summary (one sentence); the long reason stays the evidence. The server always sends
 * them (stored, or its deterministic fallback); an older server sends none, so each is optional and a bad value reads
 * as absent.
 */
/** Bounded in characters (code points), as the server and Postgres count them, not UTF-16 units. */
const reviewCopyText = (max: number) => z.string().min(1).refine((v) => [...v].length <= max).optional().catch(undefined);
export const reviewItemCopy = { title: reviewCopyText(80), summary: reviewCopyText(160), issue_label: reviewCopyText(40) };
/**
 * AIC-372, on every review-item payload beside the copy: topic (the field's label, else the issue type's), guidance
 * ("Before you decide"), blocks_results (it holds the linked result's Approve now, by the server's approve rules),
 * priority (0 blocks results, 1 vendor waiting, 2 a figure, 3 other: "prerequisites first", lower first) and suggestion
 * (why it is the suggested next step). Optional: an older server sends none.
 */
export const reviewItemPlan = {
  topic: reviewCopyText(120), guidance: reviewCopyText(400),
  blocks_results: z.boolean().optional().catch(undefined), priority: z.number().int().min(0).max(3).optional().catch(undefined),
  suggestion: reviewCopyText(160), field_id: nullableStr.optional(),
};
/** assignment_id: the item's assignment, so Today links a held result to its exact assignment (an older server sends none). */
export const queueReviewItem = z.object({ id: z.string(), kind: z.string(), reason: z.string(), company: z.string(), assignment_id: nullableStr.optional().catch(undefined), ...reviewItemCopy, ...reviewItemPlan }).loose();
/** A queue row's cycle: its lifecycle state and key dates (YYYY-MM-DD), AIC-181. */
export const queueCycle = z.object({
  id: z.string(), state: z.string(), is_dev: z.boolean().optional(),
  opens_on: nullableStr, invites_by: nullableStr, return_by: nullableStr, extension_until: nullableStr,
}).loose();
export type QueueCycle = z.infer<typeof queueCycle>;
const queueTotals = z.object({
 assignments:z.number(),open_review_items:z.number(),needs_you:z.number(),overdue:z.number(),matching:z.number(),matching_review_items:z.number(),
 by_status:z.record(z.string(),z.number()),by_display_status:z.record(z.string(),z.number()),
 by_company:z.array(z.object({company:z.string(),open_review_items:z.number(),assignments:z.number(),needs_you:z.number()})).optional(),
});
export const queue = z.object({
 totals:queueTotals.optional(), assignment_cursor:nullableStr.optional(), review_cursor:nullableStr.optional(), revision:z.object({assignments:z.string(),open_review_items:z.string()}).nullable().optional(), drafting:z.enum(["yes","outlook","reconnect","unsure","no"]).catch("unsure").optional(),
  mapped: z.boolean(),
  scope: z.string(),
  analyst: z.object({ id: z.string(), owner_name: z.string(), upn: nullableStr }).loose().nullable(),
  message: z.string().optional(),
  assignments: z.array(queueAssignment),
  open_review_items: z.array(queueReviewItem),
  cycles: z.array(queueCycle).optional(),
  /** The last successful Snowflake sync (ISO), or null when none is recorded. */
  last_sync_at: nullableStr.optional(),
  /**
   * The paged answer's filters as applied (AIC-334); `outlook` is the Outlook state its cursors are bound to, the
   * server's own read when none was sent (AIC-992), so a next page sends it back.
   */
  filters: z.object({ outlook: z.enum(["connected", "disconnected", "reconnect", "unknown"]).optional().catch(undefined) }).loose().optional(),
}).loose();
export const pagedQueue = queue.omit({assignments:true,open_review_items:true}).extend({
 paged:z.literal(true),totals:queueTotals,
 assignments:z.object({items:z.array(queueAssignment),next_cursor:nullableStr}).nullable(),
 open_review_items:z.object({items:z.array(queueReviewItem),next_cursor:nullableStr}).nullable(),
}).transform(d=>({...d,assignments:d.assignments?.items??[],open_review_items:d.open_review_items?.items??[],assignment_cursor:d.assignments?.next_cursor,review_cursor:d.open_review_items?.next_cursor}));
export type Queue = z.infer<typeof queue>;

export const portfolioSummary = z.object({
  as_of_date: z.string(), scope_total: z.number().int().nonnegative(),
  views: z.object({ all: z.number(), action: z.number(), waiting: z.number(), completed: z.number(), other: z.number() }),
  issues: z.object({ missing: z.number(), overdue: z.number(), review: z.number() }),
  mine_available: z.boolean(), effective_owner_id: nullableStr,
  /** AIC-348 parity: the cycle the portfolio answers for (cycle=current resolved; null for every cycle), and whose rows
   * owner_id=default resolved to (mine when the caller is mapped to an analyst, else all they may read). */
  cycle: nullableStr.optional(), owner_scope: z.enum(["mine", "all", "analyst"]).optional().catch(undefined),
});
export const assignmentListRow = z.object({
  portfolio_group: z.enum(["action", "waiting", "completed", "other"]).optional(),
  next_step: z.lazy(() => nextStep).optional(), next_step_label: z.string().optional(),
  has_active_contact: z.boolean().optional(), updated_at: iso.optional(), last_activity: lastActivity,
  due_kind: z.enum(["send_by", "return_by", "extension_until"]).nullable().optional(), due_on: nullableStr.optional(),
  days_left: z.number().nullable().optional(), overdue: z.boolean().optional(),
  id: z.string(), cycle: z.string(), company: z.string(), survey_type: z.string(), status: z.string(),
  owner: nullableStr, owner_upn: nullableStr, spec_status: nullableStr, result_status: nullableStr, reassigned: z.boolean().optional(),
  ...rowExtras,
}).loose();
/** The filter menus' choices in the caller's scope (AIC-181), independent of the filters applied. */
export const assignmentOptions = z.object({
  cycles: z.array(z.string()), survey_types: z.array(z.string()),
  owners: z.array(z.object({ id: z.string(), name: z.string() }).loose()), unassigned: z.boolean(),
}).loose();
export type AssignmentOptions = z.infer<typeof assignmentOptions>;
/**
 * AIC-268: a server-paged list's page info. total counts every row matching the filters and search in the caller's
 * scope; next_cursor asks for the next page (null on the last). An older server sends neither: the page is everything.
 */
const paged = { total: z.number().int().nonnegative().optional(), next_cursor: nullableStr.optional() };
export const assignmentList = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), assignments: z.array(assignmentListRow),
  options: assignmentOptions.optional(), portfolio: portfolioSummary.optional(), ...paged,
}).loose();
/** GET /me/counts (AIC-181): the rail's counts, in the caller's scope. */
export const navCounts = z.object({
  mapped: z.boolean(), today: z.number(), review: z.number(), results: z.number(),
  /** AIC-511: the bell's unread count. null: no bell (analyst preview, or before migration 0058); absent from an older server. */
  notifications: z.number().nullable().optional().catch(null),
}).loose();
export type NavCounts = z.infer<typeof navCounts>;

/** GET /me/notifications (AIC-511): the bell's drawer, newest first, in the caller's scope. In-app only. */
const notificationItem = z.object({
  id: z.string(), kind: z.string(), label: z.string(), at: z.string(), unread: z.boolean(),
  // AIC-815: null for the outcome of a "Report a mismatch" (kind profile_report_closed), which belongs to My profile.
  assignment: z.object({ id: z.string(), company: z.string(), cycle_id: z.string() }).loose().nullable(),
  profile: z.object({ outcome: z.enum(["resolved", "dismissed"]), reported: z.string(), admin_note: nullableStr }).loose().optional(),
}).loose();
export const notificationList = z.object({
  mapped: z.boolean(), last_seen_at: z.string().nullable(), seen_through: z.string().nullable(), unread: z.number(), items: z.array(notificationItem),
}).loose();
export const notificationsSeen = z.object({ last_seen_at: z.string().nullable() }).loose();
export type NotificationItem = z.infer<typeof notificationItem>;
export type NotificationList = z.infer<typeof notificationList>;
export type AssignmentList = z.infer<typeof assignmentList>;

export const sessionSummary = z.object({
  session_id: z.string(), status: z.string(), mode: z.string(), form_only: z.boolean(), scheduled_for: nullableStr, scheduled_tz: nullableStr,
  expires_at: iso, created_at: iso, code_pending: z.boolean(),
  /** AIC-239: a voice call went live on this link, so it has a call record (and, with consent, a recording). */
  had_call: z.boolean().optional().catch(undefined),
  /**
   * AIC-251: the status to show (an open link past its expiry is `expired` before the sweep moves it), whether the
   * link is closed (superseded, expired, abandoned, locked: not work in progress), the newer link that replaced it,
   * and whether it is replaced once its live call ends.
   */
  display_status: z.string().optional().catch(undefined),
  closed: z.boolean().optional().catch(undefined),
  superseded_by: nullableStr.optional().catch(undefined),
  supersede_pending: z.boolean().optional().catch(undefined),
  /**
   * AIC-317: a voice call is live on this link now (the only "in the interview now" signal; a stored in_progress is
   * also a form or a dropped call), and the outreach mode the link was issued under (build: an IDC test session).
   */
  call_live: z.boolean().optional().catch(undefined),
  outreach_mode: z.enum(["build", "live"]).optional().catch(undefined),
  /**
   * AIC-487: when the interview on this link first started (the panel's elapsed minutes), and, while the link still waits
   * for a code, when the last code minted for it ran out unused. Times only, never the code; an older server sends neither.
   */
  started_at: nullableStr.optional().catch(undefined),
  code_expired_at: nullableStr.optional().catch(undefined),
  /** AIC-534: the result this link is a follow-up interview of; not the assignment's own interview link. An older server sends none. */
  follow_up_of: nullableStr.optional().catch(undefined),
  /** AIC-252: how this link's interview ended, whether the vendor can resume on it, and its end and resume history. */
  interview: interviewOutcome.omit({ session_id: true }).extend({
    partial_verify_requested: z.boolean().optional(),
    history: z.array(z.object({ kind: z.string(), at: z.string(), via: z.string().optional(), end_reason: z.string().optional() }).loose()).optional(),
  }).loose().optional().catch(undefined),
}).loose();
export type SessionSummary = z.infer<typeof sessionSummary>;
/** POST /sessions/:id/verify-partial (AIC-252). */
export const partialVerification = z.object({ session_id: z.string(), verifier_job_id: z.string(), answered: z.number(), total: z.number() }).loose();
/** GET /sessions/:id/verifier (AIC-320, admin only): the latest verifier job and whether an admin can re-run it. */
export const verifierStatus = z.object({
  session_id: z.string(),
  latest_job: z.object({ id: z.string(), status: z.string(), updated_at: iso }).loose().nullable(),
  can_rerun: z.boolean(),
  blocked: z.string().nullable(),
}).loose();
export type VerifierStatus = z.infer<typeof verifierStatus>;
/** POST /sessions/:id/verifier/rerun (AIC-320). */
/** previous_job_id / previous_status are null for a follow-up interview whose verifier never ran (AIC-520). */
export const verifierRerun = z.object({ session_id: z.string(), verifier_job_id: z.string(), previous_job_id: nullableStr, previous_status: nullableStr }).loose();

/** AIC-304: who the assignment's next step waits on, and what it is (tracker-api routes/next-step.ts). */
export const nextStep = z.object({
  actor: z.enum(["agent", "analyst", "vendor", "system", "none"]), action: z.string(), label: z.string(), reason: z.string(),
  /** The label for a read-only viewer (a manager, an admin's preview): third person, never "your". */
  observer_label: z.string().optional(),
}).loose();
export type NextStepView = z.infer<typeof nextStep>;
export const checklistItem = z.object({
  key: z.string(), label: z.string(),
  // AIC-317 unconfirmed: a later step started but nothing records this one finishing (an invite drafted, send not
  // detected). Any value this build doesn't know reads as unknown.
  status: z.enum(["done", "actionable", "waiting", "blocked", "upcoming", "unknown", "closed", "unconfirmed"]).catch("unknown"),
  detail: nullableStr,
}).loose();
export type ChecklistItemView = z.infer<typeof checklistItem>;
/**
 * AIC-317: how the caller relates to the assignment (tracker-api routes/assignments.ts viewerRelation). Only `owner`
 * is "You". other_analyst: someone else who can read it; an unknown value reads the same way (never "You").
 */
export const viewer = z.object({
  relation: z.enum(["owner", "admin", "manager", "preview", "other_analyst"]).catch("other_analyst"),
}).loose();
export type ViewerView = z.infer<typeof viewer>;
/**
 * AIC-317: the invite draft's state on the detail (no Outlook link, no recipients). mine: the draft is in the caller's
 * own mailbox and this isn't a preview (the invite-draft route's rule, AIC-316).
 */
export const inviteSummary = z.object({
  state: z.enum(["none", "drafted", "sent"]), mailbox_upn: nullableStr, mine: z.boolean(), created_at: nullableStr, sent_detected_at: nullableStr,
}).loose();
export type InviteSummaryView = z.infer<typeof inviteSummary>;

/** The latest interview-prep job (AIC-119), as the detail and GET /assignments/:id/prep give it. */
const prepJob = z.object({ status: z.string(), created_at: iso, updated_at: iso }).loose();
/**
 * GET /assignments/:id/prep (AIC-384): what the prep watcher compares (screens/usePrepWatch.ts prepSignature): the
 * assignment's status, the latest prep job and the latest spec's id and status. The same access as the detail.
 */
export const assignmentPrep = z.object({
  id: z.string(), status: z.string(), prep_job: prepJob.nullable(), latest_spec: z.object({ id: z.string(), status: z.string() }).loose().nullable(),
}).loose();
export type AssignmentPrep = z.infer<typeof assignmentPrep>;

/**
 * GET /assignments/:id/interview-status (AIC-495): what the interview watch's signature compares and nothing else
 * (statuses, counts and times: no code, token or link). `next_step` is the action only.
 */
export const interviewStatus = z.object({
  id: z.string(), status: z.string(),
  next_step: z.object({ action: z.string() }).loose().nullable(),
  checklist: z.array(z.object({ key: z.string(), status: z.string(), detail: z.string().nullable() }).loose()),
  sessions: z.array(z.object({
    session_id: z.string(), status: z.string(), display_status: z.string().nullable().optional(), closed: z.boolean().nullable().optional(),
    call_live: z.boolean().nullable().optional(), mode: z.string(), code_pending: z.boolean(), code_expired_at: z.string().nullable().optional(),
    started_at: z.string().nullable().optional(),
    interview: z.object({ outcome: z.string().nullable().optional(), answered_count: z.number().nullable().optional(), answer_total: z.number().nullable().optional(), ended_at: z.string().nullable().optional() }).loose().nullable().optional(),
  }).loose()),
  results: z.array(z.object({ id: z.string(), status: z.string() }).loose()),
}).loose();
export type InterviewStatus = z.infer<typeof interviewStatus>;

export const assignmentDetail = z.object({
  assignment: z.object({
    id: z.string(), cycle: z.string(), company: z.string(), survey_type: z.string(), survey_priority: z.string(), status: z.string(),
    owner: nullableStr, owner_upn: nullableStr, modeler: nullableStr, headquarters: nullableStr, fiscal_year_end: nullableStr, company_url: nullableStr,
    sent_at: nullableStr, updated_at: iso,
    // owner / owner_upn are the effective analyst; these are the Snowflake owner and the reassignment (AIC-116).
    snowflake_owner: nullableStr.optional(), snowflake_owner_upn: nullableStr.optional(), reassigned: z.boolean().optional(),
    override_by: nullableStr.optional(), override_at: nullableStr.optional(), override_reason: nullableStr.optional(),
  }).loose(),
  can_reassign: z.boolean().optional(),
  latest_spec: z.object({ id: z.string(), version: z.number(), status: z.string(), approved_by: nullableStr, approved_at: nullableStr, created_at: iso, questions: z.number() }).loose().nullable(),
  sessions: z.array(sessionSummary),
  results: z.array(z.object({ id: z.string(), status: z.string(), tier: nullableStr, session_id: z.string(), created_at: iso }).loose()),
  open_review_items: z.number(),
  /** AIC-372: the preview's latest event, due date (as the queue words it; null without a live cycle date) and the
   * company's primary active contact (read as the contacts list is; null when there is none). */
  last_activity: lastActivity,
  due: z.object({ due_kind: z.enum(["send_by", "return_by", "extension_until"]), due_on: z.string(), days_left: z.number(), overdue: z.boolean() }).loose().nullable().optional().catch(undefined),
  contact: z.object({ name: nullableStr, email: z.string() }).loose().nullable().optional().catch(undefined),
  build_mode: z.boolean(),
  // The latest interview-prep job and whether the caller may queue one now (AIC-119).
  prep_job: prepJob.nullable().optional(),
  can_prepare: z.boolean().optional(),
  // false: the caller reads this assignment read-only (a manager over their reporting chain, AIC-124 follow-up).
  can_decide: z.boolean().optional(),
  // AIC-304: tracker-api's next step (@swtracker/core/jobs/next-step) and the checklist from the same state. The page
  // shows them as given; it never works them out itself. Absent from an older API: the hero line and checklist hide.
  next_step: nextStep.optional().catch(undefined),
  checklist: z.array(checklistItem).optional().catch(undefined),
  // AIC-317: absent from an older API (or malformed), the page keeps its current behaviour.
  viewer: viewer.optional().catch(undefined),
  invite: inviteSummary.optional().catch(undefined),
}).loose();
export type AssignmentDetail = z.infer<typeof assignmentDetail>;

const sourceStatus = z.enum(["compared", "none", "unavailable"]);
const specQuestion = z.object({
  field_id: z.string(), mode: z.string(), prompt: z.string(), unit: z.string(), idc_value: z.union([z.number(), z.string()]).nullable(),
}).loose();
export type SpecQuestion = z.infer<typeof specQuestion>;
const specFlag = z.object({ field_id: z.string(), kind: z.string(), reason: z.string(), advisory: z.boolean().optional() }).loose();
export type SpecFlag = z.infer<typeof specFlag>;
export const specView = z.object({
  id: z.string(), assignment_id: z.string().optional(), company: z.string(), version: z.number(), status: z.string(),
  spec: z.object({
    survey_type: z.string().optional(), opening: z.string(), closing: z.string(),
    known_facts: z.array(specQuestion),
    topics: z.array(z.object({ id: z.string(), title: z.string(), questions: z.array(specQuestion) }).loose()),
  }).loose(),
  snapshot: z.unknown(),
  can_decide: z.boolean().optional(),
  /** AIC-507: the figures that need a look, computed by tracker-api (specs/flags.ts). Absent on an older API: nothing is marked. */
  flags: z.array(specFlag).optional().catch(undefined),
  /** Which histories the flags were compared with: the previous cycle's snapshot (its id, or null) and any stored vendor answers. */
  flag_sources: z.object({ previous_cycle: z.string().nullable(), previous: sourceStatus, vendor_answers: sourceStatus }).loose().optional().catch(undefined),
  /** Why it was rejected, when a reason was given (AIC-181). */
  rejected_reason: nullableStr.optional(),
}).loose();
export type SpecView = z.infer<typeof specView>;
export const prepareResponse = z.object({ id: z.string(), job_id: z.string(), variant: z.string(), status: z.literal("queued") }).loose();
export type PrepareResponse = z.infer<typeof prepareResponse>;

export const decision = z.object({
  id: z.string(), status: z.string(),
  /** AIC-463: approving in practice also creates the practice invite, whose live link is answered once (practice_link). */
  invite: z.object({ practice_link: z.string().optional().catch(undefined) }).loose().optional().catch(undefined),
}).loose();

export const issuedLink = z.object({
  session_id: z.string(), link: z.string(), expires_at: iso, form_only: z.boolean(), build_mode: z.boolean(), notice: z.string().optional(),
  /** AIC-251: the older open links this one replaced; `deferred` had a live call and are replaced when it ends. */
  replaced: z.object({ superseded: z.number(), deferred: z.number() }).optional().catch(undefined),
}).loose();
/** POST /sessions/:id/expire (admin, AIC-251). */
export const expiredLink = z.object({ session_id: z.string(), status: z.literal("expired") }).loose();
export type IssuedLink = z.infer<typeof issuedLink>;
/** An invite draft in the analyst's own Outlook (AIC-108). Drafts only: nothing is ever sent by the system. */
export const inviteDraft = z.object({
  kind: z.literal("invite"), mode: z.enum(["build", "live", "practice"]), mailbox_upn: z.string(), to: z.array(z.string()), intended_recipients: z.array(z.string()),
  web_link: nullableStr, template_id: nullableStr, replaced_count: z.number(), created_at: iso, sent_detected_at: nullableStr,
}).loose();
export type InviteDraft = z.infer<typeof inviteDraft>;
/** GET /api/portal/assignments/:id/invite-draft */
export const inviteDraftStatus = z.object({
  // can_create: you may create a draft AND the status is in the invite window; status_allows_draft: the window alone (AIC-227).
  available: z.boolean(), build_mode: z.boolean(), can_create: z.boolean(), status_allows_draft: z.boolean(),
  outlook: z.object({ mapped: z.boolean(), connected: z.boolean(), reconnect_needed: z.boolean(), upn: nullableStr }).loose(),
  draft: inviteDraft.nullable(),
  // AIC-316: the draft is in the caller's own mailbox (only then are web_link and intended_recipients filled). A
  // missing or malformed value reads as not mine.
  mine: z.boolean().catch(false),
  // AIC-433: Mark as sent is offered (owner or admin, invite drafted and not yet sent, not a preview). Missing reads as not offered.
  can_mark_sent: z.boolean().optional().catch(undefined),
  /** AIC-463: the answer came from a practice session (the outbox, never Outlook). */
  practice: z.boolean().optional().catch(undefined),
}).loose();
export type InviteDraftStatus = z.infer<typeof inviteDraftStatus>;
/** POST /api/portal/assignments/:id/mark-sent (AIC-433): records a send that already happened; sends nothing. */
export const MARK_SENT_NOTE_MAX = 500;
export const markSentResult = z.object({
  status: z.enum(["marked", "already_sent"]), assignment_id: z.string(), sent_at: nullableStr, draft_id: nullableStr.optional(),
}).loose();
export type MarkSentResult = z.infer<typeof markSentResult>;
/** POST /api/portal/assignments/:id/invite-draft (2xx answers; not connected and refusals are ApiErrors with a message). */
export const inviteDraftResult = z.object({
  status: z.enum(["created", "replaced", "exists"]), draft: inviteDraft, attachment: z.enum(["attached", "not_needed", "not_imported", "listed (practice)"]).optional().catch(undefined),
  /** The open links the draft's new link replaced (routes/invites.ts outcomeBody; `deferred` had a live call and stop when it ends). */
  links_replaced: z.object({ superseded: z.number(), deferred: z.number() }).optional().catch(undefined),
  /** AIC-463: inside a practice session, the live interview link, answered once and never stored. */
  practice_link: z.string().optional().catch(undefined),
}).loose();
export type InviteDraftResult = z.infer<typeof inviteDraftResult>;
/** One question of a vendor follow-up (AIC-261). concern_id: the AIC-258 concern it came from, when it did. */
export const followUpQuestion = z.object({ concern_id: nullableStr, text: z.string() }).loose();
export type FollowUpQuestion = z.infer<typeof followUpQuestion>;
/** A result's vendor follow-up draft in the analyst's own Outlook (AIC-261). Drafts only: nothing is ever sent by the system. */
export const followUpDraft = z.object({
  kind: z.literal("follow_up"), result_id: z.string(), mode: z.enum(["build", "live", "practice"]), mailbox_upn: z.string(), to: z.array(z.string()),
  intended_recipients: z.array(z.string()), web_link: nullableStr, template_id: nullableStr, questions: z.array(followUpQuestion),
  replaced_count: z.number(), created_at: iso, updated_at: iso, sent_detected_at: nullableStr,
  /** AIC-429: set when the draft was recorded after it stopped being needed (the cycle closed while Outlook worked). */
  recorded_after: nullableStr.optional(), recorded_after_note: nullableStr.optional(),
}).loose();
export type FollowUpDraft = z.infer<typeof followUpDraft>;
/** GET /api/portal/results/:id/follow-up-draft. state: none (no draft yet), drafted, or sent (the analyst sent it; detected, never sent by us). */
export const followUpDraftStatus = z.object({
  available: z.boolean(), build_mode: z.boolean(), can_create: z.boolean(), cycle_closed: z.boolean(),
  state: z.enum(["none", "drafted", "sent"]), web_link: nullableStr,
  outlook: z.object({ mapped: z.boolean(), connected: z.boolean(), reconnect_needed: z.boolean(), upn: nullableStr }).loose(),
  draft: followUpDraft.nullable(),
  /** AIC-505: the result's newest follow-up interview (merged: its answers are in this result), and how many figures a new one would ask about. */
  interview: z.object({ session_id: z.string(), status: z.string(), fields: z.number(), created_at: iso, expires_at: iso, merged: z.boolean(), verifier: nullableStr.optional(),
    /** AIC-520: past its expiry (expired, or an open status whose expiry has passed). */
    link_expired: z.boolean().optional(),
    /** AIC-535: closed without its answers; held: it holds Approve (a usable link, or answers not merged and not discarded). */
    discarded: z.boolean().optional(), held: z.boolean().optional(),
    /** AIC-535: closing would discard unmerged answers of some follow-up of the result. */ releasable: z.boolean().optional(),
    /** AIC-986: how many vendor answers closing would discard (named in the confirm). */ releasable_answers: z.number().int().nonnegative().optional().catch(undefined) }).loose().nullable().optional().catch(null),
  interview_fields: z.number().int().nonnegative().optional().catch(0),
}).loose();
export type FollowUpDraftStatus = z.infer<typeof followUpDraftStatus>;
/** POST /api/portal/results/:id/follow-up-draft's body: 1 to 20 questions, each 1 to 1000 characters; concern_id is a live concern of the result (resultConcern.id). */
export const followUpDraftRequest = z.object({
  questions: z.array(z.object({ concern_id: z.uuid().optional(), text: z.string().trim().min(1).max(1000) })).min(1).max(20),
  /** AIC-505: also issue a follow-up interview link (only the concerns' figures) and put it in the draft. */
  interview: z.boolean().optional(),
});
export type FollowUpDraftRequest = z.infer<typeof followUpDraftRequest>;
/**
 * POST /api/portal/results/:id/follow-up-draft (2xx answers; not connected and refusals are ApiErrors with a message).
 * created: the result's first draft; updated: the same Outlook draft rewritten; replaced: a new draft (the old one was
 * sent, deleted or in another mailbox).
 */
export const followUpDraftResult = z.object({
  status: z.enum(["created", "updated", "replaced"]), draft: followUpDraft,
  /** AIC-505: the follow-up interview the draft now carries; practice_link: in practice, the link itself, answered once. */
  interview: z.object({ session_id: z.string(), fields: z.number(), expires_at: iso }).loose().nullable().optional(),
  practice_link: z.string().optional(),
  /** AIC-505: the link couldn't be issued after Outlook took the draft; link_removed: the draft was rewritten without it. */
  interview_refused: z.object({ reason: z.string(), message: z.string(), link_removed: z.boolean() }).loose().optional(),
}).loose();
export type FollowUpDraftResult = z.infer<typeof followUpDraftResult>;
/** POST /api/portal/results/:id/follow-up-interview/close (AIC-505): the follow-up links expired; 409 call_live while the vendor is on the call. */
/** AIC-535: discarded: answered follow-ups closed without their answers; verifying: left because their verifier is still running. */
export const followUpInterviewClosed = z.object({ status: z.enum(["closed", "none_open"]), closed: z.array(z.string()), call_live: z.array(z.string()),
  discarded: z.array(z.string()).optional(), verifying: z.array(z.string()).optional() }).loose();
export type FollowUpInterviewClosed = z.infer<typeof followUpInterviewClosed>;
export const otpView = z.object({ session_id: z.string(), code: z.string(), expires_at: iso, build_mode: z.boolean(), notice: z.string().optional() }).loose();
export type OtpView = z.infer<typeof otpView>;

export const resultListRow = z.object({
  id: z.string(), status: z.string(), tier: nullableStr, created_at: iso, company: z.string(), cycle: z.string(), assignment_id: z.string(),
  owner: nullableStr.optional(), fields: z.number(), red_flags: z.number(), decided_by: nullableStr.optional(),
  /** AIC-372: the survey type, the session's open review items, and the verifier's one-line summary (null without one). */
  survey_type: z.string().optional().catch(undefined), open_review_items: z.number().int().nonnegative().optional().catch(undefined),
  summary_line: nullableStr.optional().catch(undefined),
}).loose();
export const resultList = z.object({ mapped: z.boolean(), scope: z.string(), message: z.string().optional(), results: z.array(resultListRow), ...paged }).loose();
export type ResultList = z.infer<typeof resultList>;

/** Where a prior-cycle answer came from (AIC-262): Aurora approved results, SW_TRACKER FEEDBACK_RESULT, or a returned survey. */
export const priorSource = z.enum(["aurora", "sw_tracker", "survey_snapshot"]);
export type PriorSource = z.infer<typeof priorSource>;
/** The vendor's latest earlier-cycle answer for exactly this field id (AIC-262); null = no history. */
export const priorAnswer = z.object({
  value: z.union([z.number(), z.string()]).nullable(),
  basis: z.enum(["calendar", "fiscal"]).nullable(),
  cycle: z.string(),
  source: priorSource,
}).loose();
export type PriorAnswer = z.infer<typeof priorAnswer>;
export const resultField = z.object({
  field_id: z.string(), unit: nullableStr, idc_value: z.unknown(), vendor_value: z.unknown(), status: nullableStr,
  quote: nullableStr, verdict: nullableStr, evidence: nullableStr,
  /** AIC-551: the answer itself is confidential, so its evidence is not exported (`sensitive` is also true under an open hold). */
  evidence_withheld: z.boolean().optional().catch(undefined),
  /** AIC-262: absent from an older API; a malformed value reads as no history. */
  prior: priorAnswer.nullable().optional().catch(null),
}).loose();
export type ResultField = z.infer<typeof resultField>;
export const resultDetail = z.object({
  id: z.string(), status: z.string(), tier: nullableStr, company: z.string(), cycle: z.string(), assignment_id: z.string().optional(),
  /** The interview session the result is for: the call player (AIC-239) loads its calls. */
  session_id: z.string().optional().catch(undefined),
  fields: z.array(resultField),
  can_decide: z.boolean().optional(),
  /** AIC-182: a link to the interview's call record; null until tracker-api has a route for one. */
  call_record_url: nullableStr.optional().catch(undefined),
  /** AIC-258/259: the result's concerns and the approve gate (resultConcerns below; the Results UI is wired later). */
  concerns: z.lazy(() => z.array(resultConcern)).optional().catch(undefined),
  concern_gate: z.lazy(() => concernGate).optional().catch(undefined),
  /** AIC-262: prior-cycle sources that could not be read; a field's null prior is then "not known", not "no history". */
  prior_unavailable: z.array(priorSource).optional().catch(undefined),
}).loose();
export type ResultDetail = z.infer<typeof resultDetail>;
export const resultDecision = z.object({ id: z.string(), status: z.string(), rows_merged: z.number().optional() }).loose();
export type ResultDecision = z.infer<typeof resultDecision>;

// ---------------------------------------------------------------- AIC-258/259 result concerns

/** What the verifier recommends; "Use recommendation" pre-fills CONCERN_DISPOSITION_FOR[decision]. */
export const CONCERN_DECISIONS = ["resolve", "accept_with_note", "follow_up", "follow_up_after_approval"] as const;
/** The analyst's disposition. accepted needs a note; follow_up_after_approval only on a concern that is not blocking. */
export const CONCERN_DISPOSITIONS = ["resolved", "accepted", "follow_up", "follow_up_after_approval"] as const;
export const CONCERN_DISPOSITION_FOR = {
  resolve: "resolved", accept_with_note: "accepted", follow_up: "follow_up", follow_up_after_approval: "follow_up_after_approval",
} as const satisfies Record<(typeof CONCERN_DECISIONS)[number], (typeof CONCERN_DISPOSITIONS)[number]>;
/** Below this many decisions of a type the API sends no track record (track_record null). */
export const CONCERN_TRACK_RECORD_MIN = 5;

/**
 * One calculation behind a recommendation: the tool, the verifier's input, and the tool's own output (tracker-api
 * re-runs it on save: "Worked by <tool>, not the model"). output.steps are the working, in order.
 */
export const concernCalculation = z.object({
  tool: z.enum(["calendarize_figure", "check_period_share"]),
  input: z.record(z.string(), z.unknown()),
  worked_by: z.literal("tool"),
  output: z.object({ steps: z.array(z.string()), assumptions: z.array(z.string()) }).loose(),
}).loose();
export type ConcernCalculation = z.infer<typeof concernCalculation>;

export const concernRecommendation = z.object({
  decision: z.enum(CONCERN_DECISIONS),
  confidence: z.enum(["high", "medium", "low"]),
  rationale: z.string(),
  what_would_change: z.string(),
  suggested_note: nullableStr,
  vendor_question: nullableStr,
  calculations: z.array(concernCalculation),
}).loose();
export type ConcernRecommendation = z.infer<typeof concernRecommendation>;

export const concernTrackRecord = z.object({ decisions: z.number(), used: z.number(), overrode: z.number() }).loose();

export const resultConcern = z.object({
  /** Stable concern id: the decision route's :concernId. */
  id: z.string(),
  source: z.enum(["red_flag", "review_item"]),
  review_item_id: nullableStr,
  /** The red flag category or review item kind; the track record is per type. */
  type: z.string(),
  field_id: nullableStr,
  summary: z.string(),
  /** AIC-372: the short copy (title, issue_label, one-sentence summary); `summary` above stays the long text. */
  copy: z.object(reviewItemCopy).loose().optional().catch(undefined),
  /**
   * AIC-478: the decision dialog's title, a plain finding of at most 120 characters ("First-half 2026 looks high: $315.9M
   * is 74% of all of 2025. It's usually 40–60%."): the verifier's, else derived from `summary`. Absent from an older server.
   */
  headline: z.string().optional().catch(undefined),
  /** true: a data concern (decided before approval); false: follow-up only. verifier_blocking is the verifier's class. */
  blocking: z.boolean(),
  verifier_blocking: z.boolean(),
  reclassified: z.boolean(),
  /** null for a concern saved without one (every result from before AIC-258). */
  recommendation: concernRecommendation.nullable(),
  disposition: z.enum(CONCERN_DISPOSITIONS).nullable(),
  note: nullableStr,
  recommendation_used: z.boolean().nullable(),
  decided_by: nullableStr,
  decided_at: nullableStr,
  /** AIC-505: "follow_up_interview" when the follow-up interview's verification settled it (no person clicked). */
  decided_via: z.enum(["follow_up_interview"]).nullable().optional().catch(null),
  /** "Analysts agreed with {used} of {decisions} {type} recommendations": null below CONCERN_TRACK_RECORD_MIN decisions. */
  track_record: concernTrackRecord.nullable(),
}).loose();
export type ResultConcern = z.infer<typeof resultConcern>;

/**
 * Whether Approve is gated (approve_requires_concern_decisions) and which data concerns still hold it: those not yet
 * settled, i.e. with no disposition or marked follow_up, until Resolved or accepted with a note (AIC-274).
 */
export const concernGate = z.object({ required: z.boolean(), undecided_blocking: z.array(z.string()) }).loose();
export type ConcernGate = z.infer<typeof concernGate>;

/** GET /api/portal/results/:id's concern fields (merged into resultDetail; absent from an older server). */
export const resultConcerns = z.object({ concerns: z.array(resultConcern), concern_gate: concernGate }).loose();

/** POST /api/portal/results/:id/concerns/:concernId/decision. */
export interface ConcernDecisionInput {
  disposition: (typeof CONCERN_DISPOSITIONS)[number];
  /** Required for accepted, at most 2,000 characters. */
  note?: string;
  /** Whether the analyst used the recommendation; left out, the server records whether the disposition matches it. */
  recommendation_used?: boolean;
  /** Reclassify: true data concern, false follow-up only. */
  blocking?: boolean;
}
export const concernDecisionResponse = z.object({ concern: resultConcern, concern_gate: concernGate }).loose();
export type ConcernDecisionResponse = z.infer<typeof concernDecisionResponse>;
/**
 * Approve's 409 while the gate is on (error concerns_undecided), with the data concern ids not yet settled. Other
 * decision errors: 400 note_required, blocking_concern, no_recommendation; 409 "result is <status>", cycle_closed.
 */
export const concernsUndecidedError = z.object({ error: z.literal("concerns_undecided"), message: z.string(), concern_ids: z.array(z.string()) }).loose();

const figureValue = z.union([z.number(), z.string()]).nullable();
export const reviewItemFigures = z.object({
  field_id: z.string(), label: nullableStr, period: nullableStr, period_label: nullableStr, key: nullableStr, unit: z.string(),
  idc_value: figureValue, snapshot_id: nullableStr, vendor_value: z.unknown(), vendor_status: nullableStr,
  /** The vendor asked to keep this answer confidential (flag_sensitive, AIC-210). */
  sensitive: z.boolean().optional().catch(undefined),
  reporting_basis: z.object({ basis: z.enum(["calendar", "fiscal"]).nullable(), from: z.enum(["vendor", "idc_estimate"]).nullable() }).nullable(),
}).loose();
export type ReviewItemFigures = z.infer<typeof reviewItemFigures>;
export const reviewItemQuote = z.object({
  text: z.string(), source: z.enum(["voice", "typed", "form"]), source_label: z.string(), call_offset_s: z.number().nonnegative().nullable(), sensitive: z.boolean(),
  /** item: the words this item is about; recorded_answer: the field's recorded answer quote (labelled as such). */
  of: z.enum(["item", "recorded_answer"]).optional().catch(undefined),
  /** The text was cut to the per-item or per-page cap (a "…" ends it). */
  clipped: z.boolean().optional().catch(undefined),
}).loose();
export type ReviewItemQuote = z.infer<typeof reviewItemQuote>;
/**
 * AIC-384: the result fields a Review item's evidence panel shows, sent with the item so selecting it fetches nothing:
 * its own field and every field its evidence names, in spec order, each as GET /results/:id gives it (no prior), and
 * each first-half revenue total among them paired with the prior full year. clipped: a quote or evidence text was cut
 * to the list's cap (the result keeps the whole text). null when the item's interview has no result.
 */
const reviewEvidenceFields = z.object({
  fields: z.array(resultField),
  comparisons: z.array(z.object({ half: resultField, previous: resultField, ratio: z.number() }).loose()),
}).loose();
export type ReviewEvidenceFields = z.infer<typeof reviewEvidenceFields>;

/** One resolve, dismiss or reopen of a Review item (AIC-273), oldest first; note only on a resolve or dismiss. */
export const reviewItemEvent = z.object({ action: z.enum(["resolved", "dismissed", "reopened"]), actor: z.string(), note: nullableStr, at: iso }).loose();
export type ReviewItemEvent = z.infer<typeof reviewItemEvent>;
export const reviewItem = z.object({
  id: z.string(), kind: z.string(), reason: z.string(), ...reviewItemCopy, ...reviewItemPlan, evidence: nullableStr, field_id: nullableStr, status: z.string(), created_at: iso,
  /** AIC-372: IDC's figure from the spec against the vendor's captured value (null when the item names no spec field). */
  figures: reviewItemFigures.nullable().optional().catch(undefined),
  /** AIC-372: the vendor's own words and where they came from (null when there are none). */
  quote: reviewItemQuote.nullable().optional().catch(undefined),
  /** AIC-384: the result fields its evidence panel shows (reviewEvidenceFields); absent from an older API or malformed. */
  evidence_fields: reviewEvidenceFields.nullable().optional().catch(undefined),
  resolved_by: nullableStr, resolved_at: nullableStr, assignment_id: nullableStr, session_id: nullableStr, company: nullableStr, cycle: nullableStr, owner: nullableStr,
  can_resolve: z.boolean().optional(),
  /** AIC-182: the result of the item's interview, when there is one, and a link to its call record (null for now). */
  result_id: nullableStr.optional().catch(undefined),
  transcript_url: nullableStr.optional().catch(undefined),
  /** AIC-239: where in the call the item's field was answered (seconds from the session's start); null when unknown. */
  call_offset_s: z.number().nonnegative().nullable().optional().catch(undefined),
  /** AIC-447: where the recording starts on the session's clock (a session with one recording); null: calls but no recording; absent: read the calls. */
  recording_offset_s: z.number().finite().nullable().optional().catch(undefined),
  /** AIC-273: the note given with the current resolve or dismiss (null while open or when none was given), and every
   * resolve, dismiss and reopen so far. An older server sends neither. */
  resolution_note: nullableStr.optional(),
  history: z.array(reviewItemEvent).optional(),
}).loose();
export type ReviewItem = z.infer<typeof reviewItem>;

/**
 * GET /sessions/:id/calls (AIC-238): the session's calls in order, each placed on the session's clock (start_offset_s
 * from the session's start, the clock of the transcript offsets), and the transcript lines on the same clock.
 * stereo false is an older vendor-only recording; truncated means the recording stopped at its cap. compressed (AIC-246):
 * the call has an .m4a copy the player streams natively; false (or absent, from an older server) means WAV only.
 */
export const sessionCall = z.object({
  id: z.string(), start_offset_s: z.number(), duration_s: z.number().nonnegative(), stereo: z.boolean(), truncated: z.boolean(), has_audio: z.boolean(),
  compressed: z.boolean().default(false),
}).loose();
export type SessionCall = z.infer<typeof sessionCall>;
export const callLine = z.object({ call_id: z.string(), role: z.enum(["vendor", "interviewer"]), text: z.string(), offset_s: z.number() }).loose();
export type CallLine = z.infer<typeof callLine>;
export const sessionCalls = z.object({
  session_id: z.string(), mode: z.string(), sensitive: z.boolean(), interviewer_name: z.string().catch("Orin"),
  calls: z.array(sessionCall), lines: z.array(callLine),
}).loose();
export type SessionCalls = z.infer<typeof sessionCalls>;
/** POST /sessions/:id/playback (AIC-246): the swt_play cookie is set (HttpOnly, so the page never sees it); how long it lasts. */
export const callPlayback = z.object({ session_id: z.string(), expires_in_s: z.number().positive(), expires_at: z.string() }).loose();
export type CallPlayback = z.infer<typeof callPlayback>;
/** kinds (AIC-268): the count per kind for the status and assignment, without the kind filter or search (the hero's chips). */
export const reviewItems = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), items: z.array(reviewItem), ...paged,
  kinds: z.array(z.object({ kind: z.string(), n: z.number().int().nonnegative() }).loose()).optional(),
}).loose();
export type ReviewItems = z.infer<typeof reviewItems>;
export const resolved = z.object({ id: z.string(), status: z.string(), resolved_by: z.string() }).loose();
export const reopened = z.object({ id: z.string(), status: z.literal("open") }).loose();

const usd = z.number().nullable();
export const costInterview = z.object({
  outcome: z.enum(["completed", "partial", "abandoned"]).nullable().optional(),
  session_id: z.string(), assignment_id: z.string(), company: z.string(), status: z.string(), mode: z.string(), started_at: nullableStr,
  voice_seconds: z.number().nullable(), nova_sonic_cost_usd: usd, claude_cost_usd: usd, verifier_cost_usd: usd, prep_cost_usd: usd, total_cost_usd: usd,
}).loose();
export type CostInterview = z.infer<typeof costInterview>;
const costOutcome = z.object({
  interviews: z.number().int().nonnegative(), interviews_with_cost: z.number().int().nonnegative(), total_usd: usd,
});
export const costSummary = z.object({
  by_outcome: z.object({ completed: costOutcome, partial: costOutcome, abandoned: costOutcome }).optional(),
  interviews: z.number(), interviews_with_cost: z.number(), total_usd: usd, per_interview_usd: usd, prep_usd: usd,
  by_part: z.object({ nova_sonic_cost_usd: usd, claude_cost_usd: usd, verifier_cost_usd: usd, prep_cost_usd: usd }).optional(),
}).loose();
/** AIC-512: the Cycle dashboard's pace (completed against the due date) and at-risk assignments, grouped by analyst. */
const cyclePace = z.object({
  total: z.number(), completed: z.number(), remaining: z.number(), window_days: z.number(), completed_recent: z.number(),
  per_week: z.number().nullable(), due_on: nullableStr, projected_on: nullableStr, behind_by: z.number().nullable(),
  status: z.enum(["empty", "complete", "closed", "on_pace", "behind", "no_projection", "no_due_date"]),
  no_projection_reason: z.enum(["no_completions", "stalled"]).nullable(),
}).loose();
export type CyclePace = z.infer<typeof cyclePace>;
const atRiskReason = z.enum(["not_started_day7", "reminders_no_start", "slot_missed", "spec_stale"]);
export type AtRiskReason = z.infer<typeof atRiskReason>;
const atRisk = z.object({
  groups: z.array(z.object({
    analyst_id: nullableStr, analyst_name: z.string(),
    rows: z.array(z.object({ id: z.string(), company: z.string(), status: z.string(), analyst_id: nullableStr, analyst_name: nullableStr, reasons: z.array(atRiskReason) }).loose()),
  }).loose()),
  total: z.number(), truncated: z.boolean(),
  rules: z.object({ sent_days: z.number(), reminders: z.number(), slot_grace_hours: z.number(), spec_days: z.number() }).loose(),
}).loose();
export type AtRisk = z.infer<typeof atRisk>;
export const bulkReassignResponse = z.object({ reassigned: z.number(), results: z.array(z.object({ id: z.string(), status: z.string() }).loose()) }).loose();
export type BulkReassignResponse = z.infer<typeof bulkReassignResponse>;
export const dashboard = z.object({
  mapped: z.boolean(),
  scope: z.string(),
  message: z.string().optional(),
  cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(),
  cycle: nullableStr,
  status_counts: z.array(z.object({ status: z.string(), count: z.number() })).optional(),
  assignments_total: z.number().optional(),
  anomalies_open: z.array(z.object({ kind: z.string(), n: z.number() })).nullable().optional(),
  review_items_open: z.number().optional(),
  cost: costSummary.optional(),
  pace: cyclePace.optional(),
  at_risk: atRisk.optional(),
}).loose();
export type Dashboard = z.infer<typeof dashboard>;

/** Insights → Cost (AIC-148): the cost panel that moved off the Dashboard, with the shared filters. */
export const insightsCost = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  interviews: z.array(costInterview).optional(),
  cost: costSummary.optional(),
  truncated: z.boolean().optional(),
}).loose();
export type InsightsCost = z.infer<typeof insightsCost>;
export interface CostFilter { cycle?: string; team?: string; analyst?: string; survey_type?: string; from?: string; to?: string }

// ---------------------------------------------------------------- AIC-125 cycle timeline and pacing

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** AIC-149 lifecycle states (services/tracker-api/src/cycles/lifecycle.ts). */
export const CYCLE_STATES = ["draft", "ready", "open", "extended", "closed"] as const;
export type CycleState = (typeof CYCLE_STATES)[number];
export const cycleTimeline = z.object({
  id: z.string(), opens_on: day.nullable(), invites_by: day.nullable(), return_by: day.nullable(), extension_until: day.nullable(),
  sent_target_pct: z.number(), interviewed_target_pct: z.number(), behind_unsent_days: z.number().nullable(), behind_uninterviewed_days: z.number().nullable(),
  updated_at: nullableStr.optional(), updated_by: nullableStr.optional(),
  // AIC-149
  state: z.enum(CYCLE_STATES).optional(), is_dev: z.boolean().optional(), ready_spec_pct: z.number().optional(),
  state_changed_at: nullableStr.optional(), state_changed_by: nullableStr.optional(),
}).loose();
export type CycleTimeline = z.infer<typeof cycleTimeline>;
export const adminCycles = z.array(cycleTimeline);
export interface CycleSettings {
  opens_on: string | null; invites_by: string | null; return_by: string | null; extension_until: string | null;
  sent_target_pct: number; interviewed_target_pct: number; behind_unsent_days: number | null; behind_uninterviewed_days: number | null;
  ready_spec_pct?: number; is_dev?: boolean;
}
export type CycleDates = Pick<CycleSettings, "opens_on" | "invites_by" | "return_by" | "extension_until">;

/** Why the owners item fails (AIC-249, tracker-api cycles/readiness.ts): one category per action an admin takes. */
export const ownerCategory = z.object({
  id: z.enum(["unconfirmed", "departed", "placeholder"]), label: z.string(), action: z.string(), assignments: z.number(),
  owners: z.array(z.object({ analyst_id: z.string().nullable(), name: z.string().nullable(), assignments: z.number() })),
}).loose();
export type OwnerCategory = z.infer<typeof ownerCategory>;
export const readinessItem = z.object({
  id: z.string(), label: z.string(), status: z.enum(["ok", "fail", "blocked"]), detail: z.string(),
  blocked_by: z.object({ id: z.string(), url: z.string() }).optional(), waived: z.boolean().optional(),
  categories: z.array(ownerCategory).optional(),
}).loose();
export type ReadinessItem = z.infer<typeof readinessItem>;
/** AIC-288: how many live assignments have an active vendor contact. Reported, never checked: threshold is null. */
export const contactCoverage = z.object({
  live: z.number(), with_contact: z.number(), without_contact: z.number(), pct: z.number().nullable(), threshold: z.null(),
}).loose();
export type ContactCoverage = z.infer<typeof contactCoverage>;
export const readiness = z.object({
  cycle_id: z.string(), state: z.enum(CYCLE_STATES), pass: z.boolean(), items: z.array(readinessItem), contact_coverage: contactCoverage.optional(),
}).loose();
export type Readiness = z.infer<typeof readiness>;

// ---------------------------------------------------------------- AIC-91 SharePoint import and vendor contacts

export const IMPORT_KINDS = ["survey_list", "invite_template", "invite_thank_you_template", "reminder_template", "thank_you_pdf"] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];
export const importRecord = z.object({
  id: z.string(), kind: z.enum(IMPORT_KINDS), file_name: z.string(), web_url: nullableStr, drive_item: z.string(), etag: z.string(),
  size_bytes: z.number(), sha256: z.string(), imported_by: z.string(), imported_at: iso, summary: z.record(z.string(), z.unknown()).optional(),
  source: z.enum(["sharepoint", "upload"]).optional(), cleared: z.boolean().optional(),
}).loose();
export type ImportRecord = z.infer<typeof importRecord>;
export const cycleImports = z.object({
  cycle_id: z.string(),
  connection: z.object({ ready: z.boolean(), code: z.string().optional(), message: z.string().optional() }).loose(),
  current: z.array(z.object({ kind: z.enum(IMPORT_KINDS), label: z.string(), clearable: z.boolean().optional(), latest: importRecord.nullable() }).loose()),
  history: z.array(importRecord),
}).loose();
export type CycleImports = z.infer<typeof cycleImports>;
export const importResult = z.object({
  kind: z.enum(IMPORT_KINDS), status: z.enum(["imported", "unchanged", "error"]), source: z.enum(["sharepoint", "upload"]).optional(), file_name: z.string().optional(), import_id: z.string().optional(),
  etag: z.string().optional(), error: z.object({ code: z.string(), message: z.string() }).optional(), summary: z.record(z.string(), z.unknown()).optional(),
}).loose();
export type ImportResult = z.infer<typeof importResult>;
export const importResponse = z.object({ cycle_id: z.string(), results: z.array(importResult) }).loose();
export type ImportLinks = Partial<Record<`${ImportKind}_url`, string>>;
export const clearResponse = z.object({ cycle_id: z.string(), kind: z.enum(IMPORT_KINDS), cleared: z.number(), files: z.array(z.string()) }).loose();
export type ClearResponse = z.infer<typeof clearResponse>;

/** Survey_list reconciliation (AIC-95): GET /admin/reconciliation (the picker) and /admin/cycles/:id/reconciliation. */
export const reconciliationCycles = z.object({ cycles: z.array(z.string()), dev_cycles: z.array(z.string()), default_cycle: nullableStr }).loose();
export type ReconciliationCycles = z.infer<typeof reconciliationCycles>;
export const DIFFERENCE_KINDS = ["only_snowflake", "only_survey_list", "owner", "priority", "survey_type", "ambiguous"] as const;
export type DifferenceKind = (typeof DIFFERENCE_KINDS)[number];
const sideValues = z.object({ owner: nullableStr, priority: nullableStr, survey_type: nullableStr }).loose();
export const reconciliationDifference = z.object({
  kind: z.enum(DIFFERENCE_KINDS), company: z.string(), survey_list_company: nullableStr, field: z.enum(["owner", "priority", "survey_type"]).nullable(),
  snowflake_value: nullableStr, survey_list_value: nullableStr, snowflake: sideValues.nullable(), survey_list: sideValues.nullable(), note: nullableStr,
}).loose();
export type ReconciliationDifference = z.infer<typeof reconciliationDifference>;
export const reconciliation = z.union([
  z.object({
    cycle_id: z.string(),
    snapshot: z.object({ id: z.string(), file_name: nullableStr, imported_at: iso, rows: z.number() }).loose(),
    counts: z.object({
      snowflake: z.number(), survey_list: z.number(), in_both: z.number(), only_snowflake: z.number(), only_survey_list: z.number(),
      owner: z.number(), priority: z.number(), survey_type: z.number(), ambiguous: z.number(), in_both_differing: z.number(),
    }).loose(),
    summary: z.string(),
    skipped_fields: z.array(z.string()),
    differences: z.array(reconciliationDifference),
    assignments_revision:z.string().optional(),next_cursor:nullableStr.optional(),matching:z.number().optional(),
    totals:z.object({differences:z.number(),by_kind:z.record(z.string(),z.number())}).optional(),
  }).loose(),
  z.object({ cycle_id: z.string(), snapshot: z.null(), message: z.string() }).loose(),
]);
export type Reconciliation = z.infer<typeof reconciliation>;

export const CONTACT_STATUSES = ["active", "bounced", "retired"] as const;
/** AIC-510: a contact quality warning (tracker-api routes/contact-quality.ts). `checked` is set once an analyst has looked at it. */
const CONTACT_WARNING_KINDS = ["domain_mismatch", "personal_email", "duplicate_email", "bounced"] as const;
export const contactWarning = z.object({
  kind: z.enum(CONTACT_WARNING_KINDS), message: z.string(), domain: z.string().optional(), vendor_domain: z.string().optional(),
  companies: z.array(z.string()).optional(), when: z.enum(["now", "previous", "earlier"]).optional(),
  checked: z.object({ note: z.string(), by: z.string(), at: z.string() }).loose().nullable().catch(null),
}).loose();
export type ContactWarning = z.infer<typeof contactWarning>;
const flaggedContact = z.object({
  id: z.string(), email: z.string(), status: z.string(), first_name: nullableStr, last_name: nullableStr, assignment_id: z.string(), company: z.string(),
  owner: nullableStr, warnings: z.array(contactWarning),
}).loose();
export type FlaggedContact = z.infer<typeof flaggedContact>;
/** GET /admin/cycles/:id/contact-warnings */
export const cycleContactWarnings = z.object({ cycle_id: z.string(), contacts_examined: z.number(), flagged: z.number(), open: z.number(), contacts: z.array(flaggedContact) }).loose();
export type CycleContactWarnings = z.infer<typeof cycleContactWarnings>;
export const vendorContact = z.object({
  id: z.string(), first_name: nullableStr, last_name: nullableStr, title: nullableStr, email: z.string(), phone: nullableStr, is_primary: z.boolean(),
  source: z.enum(["survey_list_import", "analyst"]), status: z.enum(CONTACT_STATUSES), edited_by: nullableStr, edited_at: nullableStr, updated_at: nullableStr,
  /** AIC-510: only the contacts list carries them (an add or an edit answers without). */
  warnings: z.array(contactWarning).optional().catch(undefined),
}).loose();
export type VendorContact = z.infer<typeof vendorContact>;
export const assignmentContacts = z.object({ assignment_id: z.string(), company: z.string(), can_edit: z.boolean(), contacts: z.array(vendorContact) }).loose();
export type AssignmentContacts = z.infer<typeof assignmentContacts>;
/**
 * AIC-506: a colleague a vendor named on the interview page ("Someone else should answer this"). proposed: not a contact
 * yet, never named in a draft; confirmed: the analyst made them an active contact; invited: their invite draft was created
 * and their link issued (the nominating link closed); dismissed. nominated_by: who nominated (the invite's contacts), or null.
 */
const NOMINATION_STATUSES = ["proposed", "confirmed", "invited", "dismissed"] as const;
const colleagueNomination = z.object({
  id: z.string(), name: z.string(), email: z.string(), note: nullableStr, status: z.enum(NOMINATION_STATUSES), nominated_by: nullableStr,
  decided_by: nullableStr, decided_at: nullableStr, created_at: z.string(), link_issued: z.boolean(),
  /** The nominating link's state when the form was sent: issued = before the one-time code, so not verified. */
  nominator_state: z.string(),
  /** The email's domain against the vendor's known ones: true, false, or null when none is known. Only true needs no acknowledgement. */
  domain_match: z.boolean().nullable(),
  /** The company's existing contact with this email (active, bounced, retired), or null. */
  contact_status: nullableStr,
}).loose();
export type ColleagueNomination = z.infer<typeof colleagueNomination>;
export const colleagueNominations = z.object({
  assignment_id: z.string(), can_act: z.boolean(), practice: z.boolean().optional().catch(undefined), nominations: z.array(colleagueNomination),
  /** Whether a colleague can be confirmed and invited now, and why not. */
  window: z.object({ open: z.boolean(), reason: nullableStr }).loose(),
  /** An open link a vendor has started on (code verified, in progress, partial): the colleague's link closes it. */
  progress_link: z.object({ state: z.string() }).loose().nullable(),
}).loose();
export type ColleagueNominations = z.infer<typeof colleagueNominations>;
export const nominationDecision = z.object({ status: z.enum(["confirmed", "dismissed"]), nomination: colleagueNomination }).loose();
export type NominationDecision = z.infer<typeof nominationDecision>;
export interface NominationAcks { acknowledge_domain_mismatch?: true; acknowledge_reactivate?: true }
/** The colleague's own invite draft (kind invite_colleague, never the original invite row). */
export const colleagueInviteDraftResult = inviteDraftResult.extend({ draft: inviteDraft.extend({ kind: z.literal("invite_colleague") }) });
export type ColleagueInviteDraftResult = z.infer<typeof colleagueInviteDraftResult>;
export interface ContactFields { first_name?: string | null; last_name?: string | null; title?: string | null; phone?: string | null; is_primary?: boolean }
export type NewContact = ContactFields & { email: string };
export type ContactEdit = ContactFields & { email?: string; status?: (typeof CONTACT_STATUSES)[number] };

export const cycleImpact = z.object({
  cycle_id: z.string(), state: z.enum(CYCLE_STATES), applies: z.boolean(), assignments: z.number(),
  became_overdue: z.number(), no_longer_overdue: z.number(), due_date_moved: z.number(),
  reminders_dropped: z.number(), reminders_added: z.number(), reminders_restated: z.number(), reminders_moved: z.number(),
  links_expiry_moved: z.number(),
  /** One line, e.g. "84 assignments become overdue, 31 reminders move". */
  summary: z.string(),
}).loose();
export type CycleImpact = z.infer<typeof cycleImpact>;

// ---------------------------------------------------------------- AIC-150 Admin → Settings

export const SETTING_KINDS = ["text", "email", "like_pattern", "integer", "choices", "reminder_cadence", "retention_period", "url", "boolean"] as const;
export const settingView = z.object({
  key: z.string(), label: z.string(), description: z.string(), kind: z.enum(SETTING_KINDS), options: z.array(z.string()).nullable(),
  value: z.unknown(), default: z.unknown(), is_default: z.boolean(), version: z.number(), updated_by: nullableStr, updated_at: nullableStr,
}).loose();
export type SettingView = z.infer<typeof settingView>;
export const settingsList = z.object({ settings: z.array(settingView) }).loose();
export const settingHistoryEntry = z.object({
  key: z.string(), version: z.number(), before: z.unknown(), after: z.unknown(), reason: z.string(), changed_by: z.string(), changed_at: iso,
}).loose();
export type SettingHistoryEntry = z.infer<typeof settingHistoryEntry>;
export const settingHistory = z.object({ key: z.string(), history: z.array(settingHistoryEntry) }).loose();
export const settingSaved = z.object({ setting: settingView, history: settingHistoryEntry.nullable(), unchanged: z.boolean() }).loose();
export type SettingSaved = z.infer<typeof settingSaved>;
export const platformValue = z.object({ key: z.string(), label: z.string(), value: z.string(), detail: z.string(), source: z.string(), since: iso }).loose();
export type PlatformValue = z.infer<typeof platformValue>;
export const platformPanel = z.object({ values: z.array(platformValue), editable: z.literal(false) }).loose();
export type PlatformPanel = z.infer<typeof platformPanel>;

// ---------------------------------------------------------------- AIC-93 retention dry run (read-only)

export const RETENTION_ANCHORS = ["created", "cycle_closed"] as const;
export const retentionPeriodValue = z.object({ days: z.number(), from: z.enum(RETENTION_ANCHORS) }).nullable();
export type RetentionPeriodValue = z.infer<typeof retentionPeriodValue>;
const retentionTotals = z.object({ count: z.number(), bytes: z.number(), oldest: nullableStr, newest: nullableStr }).loose();
export type RetentionTotals = z.infer<typeof retentionTotals>;
export const retentionClass = z.object({
  class: z.string(), label: z.string(), store: z.enum(["s3", "aurora"]), location: z.string(), period: retentionPeriodValue, period_text: z.string(),
  cutoff: nullableStr, purge: z.enum(["delete", "archive_if_held", "never"]), status: z.enum(["keep_forever", "scanned", "unavailable"]), note: nullableStr,
  would_expire: retentionTotals, would_archive: retentionTotals, would_delete: retentionTotals,
  by_cycle: z.array(retentionTotals.extend({ cycle_id: nullableStr, archive_count: z.number(), delete_count: z.number() })),
}).loose();
export type RetentionClassReport = z.infer<typeof retentionClass>;
export const retentionReport = z.object({ generated_at: iso, dry_run: z.literal(true), purge_enabled: z.boolean(), classes: z.array(retentionClass) }).loose();
export type RetentionReport = z.infer<typeof retentionReport>;

const pctOrNull = z.number().nullable();
export const pacingSummary = z.object({
  total: z.number(), sent: z.number(), interviewed: z.number(), sent_pct: pctOrNull, interviewed_pct: pctOrNull,
  sent_target_pct: pctOrNull, interviewed_target_pct: pctOrNull, flags: z.array(z.string()),
}).loose();
export type PacingSummary = z.infer<typeof pacingSummary>;
export const pacing = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: cycleTimeline.nullable(), today: day.optional(),
  /** AIC-149: false for a draft or ready cycle, whose dates aren't in use yet (no curves, no flags). */
  dates_active: z.boolean().optional(),
  overall: z.object({
    summary: pacingSummary,
    series: z.array(z.object({ date: day, sent_pct: pctOrNull, interviewed_pct: pctOrNull, sent_target_pct: pctOrNull, interviewed_target_pct: pctOrNull }).loose()),
  }).optional(),
  teams: z.array(z.object({ manager_upn: z.string(), name: z.string(), summary: pacingSummary }).loose()).nullable().optional(),
  analysts: z.array(z.object({ analyst_id: nullableStr, name: z.string(), summary: pacingSummary }).loose()).optional(),
}).loose();
export type Pacing = z.infer<typeof pacing>;

// ---------------------------------------------------------------- AIC-145 Insights → Data outcomes

const n = z.number();
const nOrNull = z.number().nullable();
export const outcomeRates = z.object({
  rated: n, confirmed: n, corrected: n, refined: n, declined: n, added: n, without_idc: n, unanswered: n,
  confirm_rate: nOrNull, correct_rate: nOrNull, refine_rate: nOrNull, decline_rate: nOrNull,
}).loose();
export const sizeStats = z.object({
  unit: z.enum(["pct", "pp"]), n, median_signed: nOrNull, median_abs: nOrNull, p90_abs: nOrNull,
  buckets: z.object({ small: n, medium: n, large: n }), excluded: z.object({ missing_idc: n, idc_zero: n, non_numeric: n }),
}).loose();
export type SizeStats = z.infer<typeof sizeStats>;
export const outcomeCell = z.object({
  key: z.string(), label: z.string(), interviews: nOrNull, suppressed: z.boolean(),
  rates: outcomeRates.nullable(), revenue: sizeStats.nullable(), shares: sizeStats.nullable(),
}).loose();
export type OutcomeCell = z.infer<typeof outcomeCell>;
const suppressible = <T extends z.ZodType>(t: T) => z.object({ suppressed: z.boolean(), value: t.nullable() }).loose();
export const outcomes = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  outreach_mode: z.string().optional(),
  interviews: n.optional(),
  build_mode: z.object({ build: n, live: n, filter: z.string() }).loose().optional(),
  suppression: z.object({ applies: z.boolean(), min_interviews: n }).loose().optional(),
  overall: outcomeCell.optional(),
  by_family: z.array(outcomeCell).optional(),
  by_market: z.array(outcomeCell).optional(),
  by_region: z.array(outcomeCell).optional(),
  by_deployment: z.array(outcomeCell).optional(),
  by_modeler: z.array(outcomeCell).optional(),
  by_analyst: z.array(outcomeCell).optional(),
  top_fields: z.array(outcomeCell).optional(),
  basis: suppressible(z.object({ revenue_answers: n, fiscal: n, calendar: n, calendar_idc: n.optional(), unspecified: n, fiscal_share: nOrNull }).loose()).optional(),
  tiers: suppressible(z.object({ gold: n, silver: n, bronze: n, failed: n, none: n }).loose()).optional(),
  review_items: suppressible(z.object({ raised: n, interviews_with_items: n, by_kind: z.record(z.string(), n), open: n, resolved: n, dismissed: n, resolved_share: nOrNull }).loose()).optional(),
  decisions: suppressible(z.object({ approved: n, approved_with_open_review_item: n, approved_with_red_flags: n, approved_with_disagreement: n, overridden: n, override_rate: nOrNull, edited: z.null() }).loose()).optional(),
  largest_corrections: z.array(z.object({
    result_id: z.string(), company: z.string(), field_id: z.string(), field_label: nullableStr.optional(), unit: nullableStr, idc: n, vendor: n, size: n, size_unit: z.enum(["pct", "pp"]), bucket: z.string(),
  }).loose()).nullable().optional(),
  provenance_mismatches: n.optional(),
}).loose();
export type Outcomes = z.infer<typeof outcomes>;
export interface OutcomeFilter { cycle?: string; team?: string; analyst?: string; survey_type?: string; build?: "include" | "exclude" | "only" }

// ---------------------------------------------------------------- AIC-146 Insights → Workflow cycle times

const dist = z.object({ n, median_days: nOrNull, p90_days: nOrNull }).loose();
export const durationCell = z.object({
  key: z.string(), label: z.string(), assignments: nOrNull, suppressed: z.boolean(),
  stages: z.array(dist.extend({ stage: z.string(), label: z.string() })).nullable(),
  end_to_end: dist.nullable(), out_of_order: nOrNull,
}).loose();
export type DurationCell = z.infer<typeof durationCell>;
const openItem = z.object({ id: z.string(), kind: z.string(), assignment_id: z.string(), company: z.string(), age_days: n }).loose();
export const workflow = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  assignments: n.optional(),
  withdrawn: n.optional(),
  suppression: z.object({ applies: z.boolean(), min_assignments: n }).loose().optional(),
  // AIC-272: PREP_AUTO_ENQUEUE (off | build_set | all) and each stage's prep next actions (tracker-api insights/workflow.ts).
  prep_auto: z.string().optional(),
  stages: z.array(z.object({ stage: z.string(), label: z.string(), waiting_on: z.string() }).loose()).optional(),
  overall: durationCell.optional(),
  by_cycle: z.array(durationCell).optional(),
  by_team: z.array(durationCell).optional(),
  by_analyst: z.array(durationCell).optional(),
  wip: z.array(z.object({
    stage: z.string(), label: z.string(), waiting_on: z.string(), count: n, median_age_days: nOrNull, max_age_days: nOrNull,
    next_actions: z.array(z.object({ action: z.string(), label: z.string(), waiting_on: z.string(), count: n }).loose()).optional(),
  }).loose()).optional(),
  ageing: z.object({
    threshold_days: n, total: n,
    items: z.array(z.object({
      assignment_id: z.string(), company: z.string(), analyst_id: nullableStr, analyst_name: nullableStr, stage: z.string(), label: z.string(), waiting_on: z.string(), age_days: n,
      next_label: nullableStr.optional(),
    }).loose()),
  }).loose().optional(),
  open_review_items: z.object({ total: n, items: z.array(openItem) }).loose().optional(),
  open_anomalies: z.object({ total: n, items: z.array(openItem) }).loose().nullable().optional(),
  reassignments: suppressible(z.object({ assignments_reassigned: n, reassignments: n, returns_to_owner: n }).loose()).optional(),
  prep_reruns: suppressible(z.object({
    prep_runs: n, reruns: n, assignments_rerun: n, superseded_specs: n, estimate_change_anomalies: nOrNull, reruns_after_estimate_change: nOrNull,
  }).loose()).optional(),
  rejected_specs: suppressible(z.object({ specs: n, assignments: n }).loose()).optional(),
}).loose();
export type Workflow = z.infer<typeof workflow>;
export interface WorkflowFilter { cycle?: string; team?: string; analyst?: string; survey_type?: string }
// ---------------------------------------------------------------- AIC-143 Insights → Vendor funnel

export const FUNNEL_STEPS = ["issued", "opened", "code_verified", "consent", "started", "review_opened", "completed", "rated"] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];
export const stepStat = z.object({
  step: z.enum(FUNNEL_STEPS), label: z.string(), reached: n, share: nOrNull, conversion: nOrNull, median_seconds: nOrNull, timed: n,
}).loose();
export type StepStat = z.infer<typeof stepStat>;
export const funnelCell = z.object({
  key: z.string(), label: z.string(), sessions: nOrNull, assignments: nOrNull, suppressed: z.boolean(),
  steps: z.array(stepStat).nullable(), assignment_steps: z.array(stepStat).nullable(), completion_rate: nOrNull,
}).loose();
export type FunnelCell = z.infer<typeof funnelCell>;
const modeOutcome = z.object({ sessions: n, started: n, completed: n, completion_rate: nOrNull }).loose();
const deviceStat = z.object({ opened: n, completed: n, completion_rate: nOrNull }).loose();
export const funnel = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  outreach_mode: z.string().optional(),
  sessions: n.optional(),
  assignments: n.optional(),
  build_mode: z.object({ build: n, live: n, filter: z.string() }).loose().optional(),
  suppression: z.object({ applies: z.boolean(), min_sessions: n }).loose().optional(),
  stall_days: n.optional(),
  overall: funnelCell.optional(),
  by_cycle: z.array(funnelCell).optional(),
  by_team: z.array(funnelCell).optional(),
  by_analyst: z.array(funnelCell).optional(),
  by_survey_type: z.array(funnelCell).optional(),
  drop_off: suppressible(z.object({
    stalled: n, in_flight: n, completed: n, locked: n, asked_for_analyst: n,
    // AIC-251: closed links among the stalled ones.
    superseded: n.optional(), expired: n.optional(),
    // AIC-252: stalled sessions whose last attempt ended partial.
    partial: n.optional(),
    by_step: z.array(z.object({ step: z.enum(FUNNEL_STEPS), label: z.string(), sessions: n, share: nOrNull }).loose()),
  }).loose()).optional(),
  modes: suppressible(z.object({ voice: modeOutcome.extend({ share: nOrNull }), form: modeOutcome.extend({ share: nOrNull }), form_only_links: n }).loose()).optional(),
  scheduling: suppressible(z.object({ scheduled: modeOutcome, start_now: modeOutcome }).loose()).optional(),
  ratings: suppressible(z.object({
    rated: n, rating_rate: nOrNull, mean: nOrNull,
    by_score: z.object({ "1": n, "2": n, "3": n, "4": n, "5": n }).loose(), prefers_ai: z.object({ yes: n, no: n, maybe: n }).loose(),
  }).loose()).optional(),
  reconnects: suppressible(z.object({ voice_started: n, with_reconnect: n, reconnects: n, reconnect_rate: nOrNull }).loose()).optional(),
  devices: suppressible(z.object({ desktop: deviceStat, mobile: deviceStat, tablet: deviceStat, unknown: deviceStat }).loose()).optional(),
  // AIC-252: sessions by interview outcome, partial counted apart from completed.
  outcomes: suppressible(z.object({
    completed: n, partial: n, abandoned: n, in_progress: n, review: n, unknown: n, not_started: n, partial_answered_share: nOrNull,
  }).loose()).optional().catch(undefined),
}).loose();
export type Funnel = z.infer<typeof funnel>;
/** The funnel takes the same filters as outcomes. */
export type FunnelFilter = OutcomeFilter;
// ---------------------------------------------------------------- AIC-144 Insights → Call quality

export const spread = z.object({ n, p50: nOrNull, p90: nOrNull, max: nOrNull }).loose();
export type Spread = z.infer<typeof spread>;
const counts = z.record(z.string(), n);
export const callOutlier = z.object({
  session_id: z.string(), assignment_id: z.string(), result_id: nullableStr, company: z.string(), started_at: z.string(),
  end_reason: z.string(), duration_ms: n, extraction_p95_ms: nOrNull, fallback_rate: nOrNull, failed_renewals: n, re_asks: n,
  reasons: z.array(z.string()),
}).loose();
export type CallOutlier = z.infer<typeof callOutlier>;
export const callQuality = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  interviews: n.optional(),
  suppressed: z.boolean().optional(),
  suppression: z.object({ applies: z.boolean(), min_interviews: n }).loose().optional(),
  distributions: z.object({
    duration_s: spread, extraction_p95_ms: spread, extraction_p50_ms: spread, model_p50_ms: spread, response_p50_ms: spread,
    first_audio_ms: spread, renewals: spread, reopen_max_ms: spread, re_asks: spread, fallback_rate: spread,
  }).loose().nullable().optional(),
  totals: z.object({
    calls: n, reconnects: n, vendor_turns: n, answers_recorded: n, held: n, held_resolved: n, dropped: counts,
    extractor_turns: n, bedrock_turns: n, fallback_rate: nOrNull, extraction_failures: n, renewals: counts, failed_renewals: n,
    interrupted: n, re_asks: n, sensitive_flags: n, form_fallback: n,
  }).loose().nullable().optional(),
  end_reasons: counts.nullable().optional(),
  hangup_guards: counts.nullable().optional(),
  outliers: z.array(callOutlier).nullable().optional(),
}).loose();
export type CallQuality = z.infer<typeof callQuality>;
export interface CallQualityFilter { cycle?: string; team?: string; analyst?: string }

/** Insights → Verifier accuracy (AIC-514; tracker-api insights/verifier-accuracy.ts): counts and rates only, no text. */
const verifierCell = z.object({
  key: z.string(), label: z.string(), sessions: nOrNull, suppressed: z.boolean(),
  units: nOrNull, agreed: nOrNull, overrode: nOrNull, corrected: nOrNull, agreement_rate: nOrNull,
}).loose();
export type VerifierCell = z.infer<typeof verifierCell>;
export const verifierAccuracy = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  suppression: z.object({ applies: z.boolean(), min_interviews: n }).loose().optional(),
  overall: verifierCell.optional(),
  by_unit: z.array(verifierCell).optional(), by_cycle: z.array(verifierCell).optional(), by_family: z.array(verifierCell).optional(), by_version: z.array(verifierCell).optional(),
  top_override_kinds: z.array(z.object({ kind: z.string(), label: z.string(), sessions: nOrNull, suppressed: z.boolean(), count: nOrNull, unit_types: z.array(z.string()) }).loose()).optional(),
}).loose();
export type VerifierAccuracy = z.infer<typeof verifierAccuracy>;
export interface VerifierAccuracyFilter { cycle?: string; team?: string; analyst?: string; survey_type?: string }

export { iso, nullableStr };

/** Practice mode (AIC-463; tracker-api routes/practice.ts and practice-outbox.ts). */
export const practiceStarted = z.object({ practice_session_id: z.string(), assignment_id: z.string(), spec_id: z.string(), created_at: iso }).loose();
export type PracticeStarted = z.infer<typeof practiceStarted>;
export const practiceSession = z.object({
  id: z.string(), status: z.string(), analyst_id: z.string(), analyst_name: nullableStr, source_assignment_id: z.string(), company: z.string(), cycle: z.string(),
  started_by: z.string(), on_behalf_of: nullableStr, created_at: iso, reset_at: nullableStr, reset_by: nullableStr, mine: z.boolean(),
}).loose();
export type PracticeSessionRow = z.infer<typeof practiceSession>;
export const practiceList = z.object({
  sessions: z.array(practiceSession),
  limits: z.object({ active_sessions: z.number() }).loose(),
  my_active_sessions: z.number(),
  can_start: z.boolean(),
}).loose();
export type PracticeList = z.infer<typeof practiceList>;
export const practiceReset = z.object({ practice_session_id: z.string(), status: z.string() }).loose();
export const practiceRestored = z.object({ practice_session_id: z.string(), assignment_id: z.string(), spec_id: z.string(), status: z.string(), changed: z.boolean() }).loose();
export type PracticeRestored = z.infer<typeof practiceRestored>;
export const practiceDraft = z.object({
  id: z.string(), kind: z.string(), assignment_id: z.string(), state: z.enum(["drafted", "sent", "discarded", "unknown"]).catch("unknown"),
  to: z.array(z.string()), subject: z.string(), body_html: z.string(), created_at: iso, sent_at: nullableStr, discarded_at: nullableStr,
}).loose();
export type PracticeDraft = z.infer<typeof practiceDraft>;
export const practiceOutbox = z.object({ practice_session_id: z.string(), drafts: z.array(practiceDraft) }).loose();
export type PracticeOutbox = z.infer<typeof practiceOutbox>;
export const practiceDraftAction = z.object({ status: z.string() }).loose();

// ---------------------------------------------------------------- AIC-513 Insights → Modeler digest

export const DIGEST_GROUPS = ["market", "region", "deployment", "vendor"] as const;
export type DigestGroup = (typeof DIGEST_GROUPS)[number];
const digestN = z.number().nullable();
const digestRow = z.object({
  result_id: z.string(), assignment_id: z.string(), company: z.string(), cycle_id: z.string(), modeler: nullableStr, tier: nullableStr,
  field_id: z.string(), field_label: z.string(), kind: z.enum(["revenue", "share"]).nullable().optional(), period: nullableStr.optional(), unit: nullableStr, market: nullableStr, region: nullableStr, deployment: nullableStr,
  idc: z.number(), vendor: z.number(), delta_abs: z.number(), delta_pct: digestN, impact_usd_m: digestN, note: nullableStr, group_key: nullableStr,
}).loose();
export type DigestRow = z.infer<typeof digestRow>;
const digestCell = z.object({
  key: z.string(), label: z.string(), suppressed: z.boolean(), interviews: digestN, fields: digestN, higher: digestN, lower: digestN,
  revenue_fields: digestN, revenue_change_usd_m: digestN, moved_market_usd_m: digestN, moved_region_usd_m: digestN, moved_deployment_usd_m: digestN,
}).loose();
export type DigestCell = z.infer<typeof digestCell>;
export const modelerDigest = z.object({
  mapped: z.boolean(), scope: z.string(), message: z.string().optional(), cycles: z.array(z.string()), dev_cycles: z.array(z.string()).optional(), cycle: nullableStr,
  teams: z.array(z.string()).optional(), analysts: z.array(z.object({ id: z.string(), name: z.string() }).loose()).optional(),
  modelers: z.array(z.string()).optional(), markets: z.array(z.string()).optional(),
  group: z.enum(DIGEST_GROUPS).optional(),
  suppression: z.object({ applies: z.boolean(), min_interviews: z.number() }).loose().optional(),
  approved_interviews: z.number().nullable().optional(),
  period: nullableStr.optional(), periods: z.array(z.string()).optional(), withheld_rows: z.number().optional(),
  overall: digestCell.optional(),
  groups: z.array(digestCell).optional(),
  rows: z.array(digestRow).nullable().optional(),
  total_rows: z.number().nullable().optional(),
  truncated: z.boolean().optional(),
}).loose();
export type ModelerDigest = z.infer<typeof modelerDigest>;
export interface DigestFilter { cycle?: string; team?: string; analyst?: string; build?: "include" | "exclude" | "only"; modeler?: string; market?: string; min_delta?: string; group?: string; period?: string }
